import * as React from "react";
import { Eye, EyeOff } from "./icons";

import { cn } from "utils/cn";
import { CopyButton } from "./CopyButton";
import { IconButton } from "./IconButton";

const HEAD = 10;
const TAIL = 6;
const MASK = "••••••••••••";

// Buttons inside the chip shrink to the chip's line height.
const innerButton = "h-6 w-6 rounded [&_svg]:h-3.5 [&_svg]:w-3.5";

/** "tenant-prod-mum…a1b2c3": keeps both ends of an identifier, which is where they differ. */
export const middleTruncate = (value: string, head = HEAD, tail = TAIL): string =>
  value.length > head + tail + 1 ? `${value.slice(0, head)}…${value.slice(-tail)}` : value;

export interface CodeValueProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  value: string;
  /** What the value is, for button names: "public key" → "Copy public key". */
  label?: string;
  /** Shortens long values to the first 10 and last 6 characters; the full value is in the title. */
  truncate?: boolean;
  copyable?: boolean;
  /** Hides the value behind dots with a show / hide toggle. Copy still copies the real value. */
  masked?: boolean;
}

/** Inline monospace identifier (tenant URI, publicKey, DB_KEY, invoice number) with copy. */
export const CodeValue = React.forwardRef<HTMLSpanElement, CodeValueProps>(
  (
    { value, label, truncate = false, copyable = true, masked = false, className, ...props },
    ref
  ) => {
    const [revealed, setRevealed] = React.useState(false);
    const hidden = masked && !revealed;
    const shown = truncate ? middleTruncate(value) : value;
    const suffix = label ? ` ${label}` : "";

    return (
      <span
        ref={ref}
        data-ui="code-value"
        className={cn(
          "inline-flex max-w-full items-center rounded-md border border-border bg-surface-muted font-mono text-xs text-foreground",
          className
        )}
        {...props}
      >
        {hidden ? (
          <>
            <span aria-hidden="true" className="px-2 py-1 tracking-wider text-muted-foreground">
              {MASK}
            </span>
            <span className="sr-only">Hidden{suffix}</span>
          </>
        ) : (
          // Full value in the title: covers `truncate` and values clipped by a narrow container.
          <code className="min-w-0 truncate px-2 py-1" title={value}>
            {shown}
          </code>
        )}
        {(masked || copyable) && (
          <span className="-ml-1 flex shrink-0 items-center pr-0.5">
            {masked && (
              <IconButton
                label={revealed ? `Hide${suffix}` : `Show${suffix}`}
                icon={revealed ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                size="sm"
                aria-pressed={revealed}
                className={cn(innerButton, "text-muted-foreground hover:text-foreground")}
                onClick={() => setRevealed((current) => !current)}
              />
            )}
            {copyable && (
              <CopyButton
                value={value}
                label={`Copy${suffix}`}
                className={cn(innerButton, "text-muted-foreground hover:text-foreground")}
              />
            )}
          </span>
        )}
      </span>
    );
  }
);
CodeValue.displayName = "CodeValue";
